import { ErrorMessage, Field } from 'formik';
import { useMemo } from 'react';
import { Col, Form, FormControl, Row, Spinner, Stack } from 'react-bootstrap';
import { FormLabel, FormStepHeader } from '../../../../../../components/forms';
import { NumberInput } from '../../../../../../components/inputs/NumberInput';
import { FormSelect } from '../../../../../../components/tables/filterForms/FormSelect';
import { useToast } from '../../../../../../context/ToastContext';
import { useGetVenuesQuery } from '../../../../../../services/endpoints/places/venue';
import { Venue } from '../../../../../../types/venue.type';
import { getErrorMessage } from '../../../../../../utils/utils';
import { StepProps } from '../AddEditSession';

export const VenueForm = ({ touched, errors, values, readOnly }: StepProps) => {
  const { data, error, isLoading } = useGetVenuesQuery({
    scope: 'active',
    data: { entries: 10000, orderBy: { field: 'title', order: 'asc' } }
  });
  const toast = useToast();

  const venueOptions = useMemo(() => {
    const fromApi =
      data?.results
        ?.filter((venue: Venue) => venue.title)
        .map((venue: Venue) => ({ value: venue.id, label: venue.title })) ?? [];
    const selected = values?.venue_id;
    const merged = [...fromApi];

    // Keep a currently selected inactive venue visible when editing a session.
    if (selected?.value != null && !merged.some((option) => option.value === selected.value)) {
      merged.push({
        value: selected.value,
        label: selected.label || `Venue ${selected.value}`
      });
    }

    return merged.sort((a, b) => a.label.localeCompare(b.label));
  }, [data?.results, values?.venue_id]);

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  if (error || !data || !data.results) {
    toast.addToast(getErrorMessage(error) ?? 'Error loading venues', 'error');
  }

  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Venue Details" showRequiredLegend={!readOnly} />
      </Col>
      <Col
        xs={12}
        lg={6}
      >
        <Stack gap={3}>
          <Form.Group controlId="venue_id">
            <FormSelect
              name="venue_id"
              label="Venue"
              options={venueOptions}
              disabled={readOnly}
              required
              loadingError={error ? 'Error loading venues' : undefined}
            />
            <ErrorMessage name="venue_id.value">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
            <Form.Text>Select a pre-existing venue to auto-fill address</Form.Text>
          </Form.Group>

          <Form.Group controlId="room">
            <FormLabel>Room</FormLabel>
            <Field
              name="room"
              type="text"
              as={Form.Control}
              isInvalid={touched.room && !!errors.room}
              disabled={readOnly}
            />
            <ErrorMessage name="room">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group controlId="capacity">
            <FormLabel required>Capacity</FormLabel>
            <Field
              name="capacity"
              min="0"
              step="1"
              as={NumberInput}
              style={{ width: 85 }}
              isInvalid={touched.capacity && !!errors.capacity}
              disabled={readOnly}
              aria-required
            />
            <ErrorMessage name="capacity">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>
        </Stack>
      </Col>
    </Row>
  );
};
